<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import AppDataTable from "@/components/AppDataTable.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useResourceList } from "@/composables/useResourceList";
import { fetchAllQuizzes, fetchQuizAnalytics, deleteQuiz } from "./api";

const router = useRouter();
const columns = [
  { key: "code",    label: "Code",    sortable: true },
  { key: "title",   label: "Title",   sortable: true },
  { key: "lang",    label: "Lang" },
  { key: "enable",  label: "Enabled" },
  { key: "actions", label: "Actions", align: "end", width: "300px" },
];

// Keep raw quiz data for the View dialog
const rawQuizzes = ref([]);
const { rows, loading, error, pendingDelete, deleting, load, askDelete, confirmDelete } =
  useResourceList({
    fetch: () => fetchAllQuizzes().then((data) => { rawQuizzes.value = data; return data; }),
    remove: deleteQuiz,
    mapRow: (q) => ({ id: q._id, code: q.code, title: q.title, lang: q.lang, enable: q.enable ? "Yes" : "No" }),
  });

// ── View dialog ──────────────────────────────────────────────────────────────
const viewDialog = ref(false);
const viewQuiz = ref(null);
function openView(row) {
  viewQuiz.value = rawQuizzes.value.find((q) => q._id === row.id) ?? null;
  viewDialog.value = true;
}

// ── Analytics dialog ─────────────────────────────────────────────────────────
const analyticsDialog = ref(false);
const analyticsData = ref(null);
const analyticsLoading = ref(false);
const analyticsTitle = ref("");
async function openAnalytics(row) {
  analyticsDialog.value = true;
  analyticsLoading.value = true;
  analyticsData.value = null;
  analyticsTitle.value = row.title;
  try {
    const res = await fetchQuizAnalytics(row.id);
    analyticsData.value = res?.data ?? res ?? null;
  } catch { analyticsData.value = null; }
  finally { analyticsLoading.value = false; }
}

function calPercent(a, b) {
  if (!b) return "0%";
  return Math.round(((a / b) * 100 + Number.EPSILON) * 100) / 100 + "%";
}
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Manage Quizzes</h1>
      <div class="d-flex gap-2">
        <VBtn variant="tonal" @click="load">Refresh</VBtn>
        <VBtn variant="tonal" @click="router.push({ name: 'quizzes-batch' })">Batch Upload</VBtn>
        <VBtn @click="router.push({ name: 'quizzes-edit', params: { id: 'new' } })">Add Quiz</VBtn>
      </div>
    </div>
    <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
    <AppDataTable :columns="columns" :rows="rows" :loading="loading" row-key="id" searchable search-placeholder="Search quizzes…" empty-text="No quizzes yet.">
      <template #cell-actions="{ row }">
        <VBtn size="small" variant="text" @click.stop="openView(row)">View</VBtn>
        <VBtn size="small" variant="text" @click.stop="openAnalytics(row)">Analytics</VBtn>
        <VBtn size="small" variant="text" @click.stop="router.push({ name: 'quizzes-edit', params: { id: row.id } })">Edit</VBtn>
        <VBtn size="small" color="error" variant="text" @click.stop="askDelete(row)">Delete</VBtn>
      </template>
    </AppDataTable>
    <ConfirmDialog :model-value="pendingDelete !== null" title="Delete quiz?" :message="`Delete &quot;${pendingDelete?.title || 'this quiz'}&quot;?`" :loading="deleting" @update:model-value="pendingDelete = null" @confirm="confirmDelete" />

    <!-- View Dialog -->
    <VDialog v-model="viewDialog" max-width="450" scrollable>
      <VCard v-if="viewQuiz">
        <VCardTitle class="pt-4 px-4">{{ viewQuiz.title }}</VCardTitle>
        <VCardSubtitle class="px-4">{{ viewQuiz.questions?.[0]?.competency }}</VCardSubtitle>
        <VCardText>
          <div class="text-body-1 font-weight-medium mb-3">{{ viewQuiz.questions?.[0]?.text }}</div>
          <div v-if="viewQuiz.questions?.[0]?.type === 'mc'" class="d-flex flex-column gap-2">
            <VChip v-for="(opt, i) in viewQuiz.questions?.[0]?.responses" :key="i" variant="tonal">{{ opt }}</VChip>
          </div>
          <div v-else class="text-body-2 text-disabled">{{ viewQuiz.questions?.[0]?.responses }}</div>
        </VCardText>
        <VCardActions class="px-4 pb-4 justify-end">
          <VBtn @click="viewDialog = false">Close</VBtn>
        </VCardActions>
      </VCard>
      <VCard v-else>
        <VCardText class="text-disabled pa-4 text-center">Quiz data not available.</VCardText>
        <VCardActions class="justify-end px-4 pb-4"><VBtn @click="viewDialog = false">Close</VBtn></VCardActions>
      </VCard>
    </VDialog>

    <!-- Analytics Dialog -->
    <VDialog v-model="analyticsDialog" max-width="520" scrollable>
      <VCard>
        <VCardTitle class="pt-4 px-4">Analytics — {{ analyticsTitle }}</VCardTitle>
        <VCardText>
          <div v-if="analyticsLoading" class="d-flex justify-center pa-4"><VProgressCircular indeterminate /></div>
          <div v-else-if="analyticsData">
            <VList density="compact" lines="one">
              <VListItem title="Total attempts" :subtitle="String(analyticsData.totalResponses ?? '—')" />
              <VListItem title="Unique users" :subtitle="String(analyticsData.users?.length ?? '—')" />
            </VList>
            <div v-if="analyticsData.answerCount?.length" class="mt-3">
              <div class="text-subtitle-2 mb-1">Correct answers distribution:</div>
              <VTable density="compact">
                <thead><tr><th>Correct</th><th>Count</th><th>%</th></tr></thead>
                <tbody>
                  <tr v-for="(item, i) in analyticsData.answerCount" :key="i">
                    <td>{{ item.correct }}</td>
                    <td>{{ item.count }}</td>
                    <td>{{ calPercent(item.count, analyticsData.totalResponses) }}</td>
                  </tr>
                </tbody>
              </VTable>
            </div>
            <div v-if="analyticsData.answerGroup?.length" class="mt-3">
              <div class="text-subtitle-2 mb-1">Answer breakdown:</div>
              <VTable density="compact">
                <thead><tr><th>Answer</th><th>Count</th><th>%</th></tr></thead>
                <tbody>
                  <tr v-for="(item, i) in analyticsData.answerGroup" :key="i">
                    <td>{{ item.answers }}</td>
                    <td>{{ item.count }}</td>
                    <td>{{ calPercent(item.count, analyticsData.totalResponses) }}</td>
                  </tr>
                </tbody>
              </VTable>
            </div>
          </div>
          <div v-else class="text-disabled pa-4 text-center">No analytics data available.</div>
        </VCardText>
        <VCardActions class="px-4 pb-4 justify-end">
          <VBtn @click="analyticsDialog = false">Close</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
